import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save,
  Loader2,
  Plus,
  Trash2,
  ChefHat,
  Package,
  Layers,
  Eye,
  Edit2,
  Timer,
  Users,
  DollarSign,
  AlertCircle,
  CheckCircle,
  X,
  Scale,
  Upload,
  Image as ImageIcon,
  Youtube,
  Video,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useLocationStore } from '@/store/location';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  type Ingredient,
  type Instruction,
  type CreateTemplateInput,
} from '@/hooks/useStandardizedTemplates';

const typeOptions = [
  { value: 'recipe', label: 'Recipe', icon: ChefHat },
  { value: 'product', label: 'Product', icon: Package },
  { value: 'bundle', label: 'Bundle', icon: Layers },
];

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'archived', label: 'Archived' },
];

const difficultyOptions = [
  { value: 'none', label: 'Not specified' },
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

const unitOptions = [
  'unit', 'g', 'kg', 'oz', 'lb', 'ml', 'L', 'cup', 'tbsp', 'tsp',
  'piece', 'slice', 'bunch', 'can', 'jar', 'package',
];

const categoryOptions = [
  'Appetizer', 'Main Course', 'Side Dish', 'Dessert', 'Beverage',
  'Breakfast', 'Lunch', 'Dinner', 'Snack', 'Sauce', 'Condiment',
  'Bread', 'Salad', 'Soup',
];

const dietaryTagOptions = [
  'Vegan', 'Vegetarian', 'Gluten-Free', 'Dairy-Free', 'Nut-Free',
  'Keto', 'Paleo', 'Low-Carb', 'Low-Sodium', 'Sugar-Free', 'Halal', 'Kosher',
];

const defaultIngredient: Ingredient = {
  name: '',
  quantity: 1,
  unit: 'unit',
  cost_per_unit: undefined,
  notes: '',
  is_optional: false,
};

const defaultInstruction: Instruction = {
  step_number: 1,
  description: '',
  duration_minutes: undefined,
  notes: '',
};

export default function TemplateEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { profile } = useAuth();
  const isNew = !id || id === 'new';

  const { data: existingTemplate, isLoading: templateLoading } = useTemplate(id || '');
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<Omit<CreateTemplateInput, 'organization_id'>>({
    location_id: currentLocation?.id,
    name: '',
    description: '',
    category: '',
    type: 'recipe',
    ingredients: [{ ...defaultIngredient }],
    instructions: [{ ...defaultInstruction }],
    prep_time_minutes: undefined,
    cook_time_minutes: undefined,
    servings: 4,
    serving_unit: 'portions',
    dietary_tags: [],
    cuisine_type: '',
    difficulty_level: undefined,
    status: 'draft',
  });

  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [scaleFactor, setScaleFactor] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sectionsOpen, setSectionsOpen] = useState({
    basic: true,
    ingredients: true,
    instructions: true,
    timing: true,
    metadata: true,
  });

  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  // Load existing template
  useEffect(() => {
    if (existingTemplate) {
      setFormData({
        location_id: existingTemplate.location_id,
        name: existingTemplate.name,
        description: existingTemplate.description || '',
        category: existingTemplate.category || '',
        type: existingTemplate.type,
        ingredients:
          existingTemplate.ingredients.length > 0
            ? existingTemplate.ingredients
            : [{ ...defaultIngredient }],
        instructions:
          existingTemplate.instructions.length > 0
            ? existingTemplate.instructions
            : [{ ...defaultInstruction }],
        prep_time_minutes: existingTemplate.prep_time_minutes,
        cook_time_minutes: existingTemplate.cook_time_minutes,
        servings: existingTemplate.servings,
        serving_unit: existingTemplate.serving_unit || 'portions',
        dietary_tags: existingTemplate.dietary_tags || [],
        cuisine_type: existingTemplate.cuisine_type || '',
        difficulty_level: existingTemplate.difficulty_level,
        status: existingTemplate.status,
      });
      setImageUrl(existingTemplate.image_url || null);
      setVideoUrl((existingTemplate as any).video_url || null);
    }
  }, [existingTemplate]);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleVideoUrlChange = (url: string) => {
    setVideoUrl(url || null);
  };

  // Handle image upload
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !profile?.organization_id) return;

    setImageUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile.organization_id}/templates/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName);

      setImageUrl(publicUrl);
      toast.success('Image uploaded');
    } catch (error: any) {
      toast.error('Upload failed', {
        description: error.message,
      });
    } finally {
      setImageUploading(false);
    }
  };

  // Calculate total cost
  const totalCost = useMemo(() => {
    return (formData.ingredients || []).reduce((sum, ing) => {
      const cost = (ing.cost_per_unit || 0) * ing.quantity;
      return sum + cost;
    }, 0);
  }, [formData.ingredients]);

  const costPerServing = useMemo(() => {
    const servings = formData.servings || 1;
    return totalCost / servings;
  }, [totalCost, formData.servings]);

  const totalTime = (formData.prep_time_minutes || 0) + (formData.cook_time_minutes || 0);

  // Scale ingredients
  const scaledIngredients = useMemo(() => {
    return (formData.ingredients || []).map((ing) => ({
      ...ing,
      quantity: ing.quantity * scaleFactor,
    }));
  }, [formData.ingredients, scaleFactor]);

  // Handlers
  const updateField = <K extends keyof typeof formData>(
    field: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addIngredient = () => {
    setFormData((prev) => ({
      ...prev,
      ingredients: [...(prev.ingredients || []), { ...defaultIngredient }],
    }));
  };

  const updateIngredient = (index: number, updates: Partial<Ingredient>) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: (prev.ingredients || []).map((ing, i) =>
        i === index ? { ...ing, ...updates } : ing
      ),
    }));
  };

  const removeIngredient = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: (prev.ingredients || []).filter((_, i) => i !== index),
    }));
  };

  const addInstruction = () => {
    const nextStep = (formData.instructions || []).length + 1;
    setFormData((prev) => ({
      ...prev,
      instructions: [
        ...(prev.instructions || []),
        { ...defaultInstruction, step_number: nextStep },
      ],
    }));
  };

  const updateInstruction = (index: number, updates: Partial<Instruction>) => {
    setFormData((prev) => ({
      ...prev,
      instructions: (prev.instructions || []).map((inst, i) =>
        i === index ? { ...inst, ...updates } : inst
      ),
    }));
  };

  const removeInstruction = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      instructions: (prev.instructions || [])
        .filter((_, i) => i !== index)
        .map((inst, i) => ({ ...inst, step_number: i + 1 })),
    }));
  };

  const toggleDietaryTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      dietary_tags: (prev.dietary_tags || []).includes(tag)
        ? (prev.dietary_tags || []).filter((t) => t !== tag)
        : [...(prev.dietary_tags || []), tag],
    }));
  };

  const handleSave = async () => {
    setError(null);

    if (!formData.name.trim()) {
      setError('Please enter a template name');
      return;
    }

    try {
      const cleanedData = {
        ...formData,
        image_url: imageUrl || undefined,
        video_url: videoUrl || undefined,
        ingredients: (formData.ingredients || []).filter((ing) => ing.name.trim()),
        instructions: (formData.instructions || []).filter((inst) =>
          inst.description.trim()
        ),
        estimated_cost_per_serving: costPerServing > 0 ? costPerServing : undefined,
      };

      if (isNew) {
        const newTemplate = await createTemplate.mutateAsync(cleanedData);
        setSuccessMessage('Template created successfully!');
        setTimeout(() => {
          navigate(`/standardization/${newTemplate.id}`, { replace: true });
        }, 1000);
      } else {
        await updateTemplate.mutateAsync({ id: id!, ...cleanedData });
        setSuccessMessage('Template saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    }
  };

  if (!isNew && templateLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl pb-20">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">
          {isNew ? 'New Template' : formData.name || 'Edit Template'}
        </h2>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('edit')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                viewMode === 'edit'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground'
              )}
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                viewMode === 'preview'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground'
              )}
            >
              <Eye className="h-4 w-4" />
              Preview
            </button>
          </div>

          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 hover:bg-destructive/20 rounded"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 dark:text-green-400 rounded-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{successMessage}</p>
        </div>
      )}

      {viewMode === 'edit' ? (
        /* Edit Mode */
        <div className="space-y-4">
          {/* Basic Info */}
          <Collapsible
            open={sectionsOpen.basic}
            onOpenChange={(open) => setSectionsOpen((s) => ({ ...s, basic: open }))}
          >
            <div className="bg-card rounded-xl border">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
                <h3 className="font-semibold">Basic Information</h3>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        placeholder="e.g., Traditional Ackee and Saltfish"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Type</Label>
                      <Select
                        value={formData.type}
                        onValueChange={(v) => updateField('type', v as any)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {typeOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(v) => updateField('status', v as any)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Category</Label>
                      <Select
                        value={formData.category || ''}
                        onValueChange={(v) => updateField('category', v)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select category..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryOptions.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Cuisine Type</Label>
                      <Input
                        value={formData.cuisine_type || ''}
                        onChange={(e) => updateField('cuisine_type', e.target.value)}
                        placeholder="e.g., Jamaican, Italian"
                        className="mt-1"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label>Description</Label>
                      <Textarea
                        value={formData.description || ''}
                        onChange={(e) => updateField('description', e.target.value)}
                        rows={3}
                        placeholder="Describe the recipe or product..."
                        className="mt-1"
                      />
                    </div>

                    {/* Image Upload */}
                    <div className="md:col-span-2">
                      <Label>Photo</Label>
                      <div className="mt-1 flex items-start gap-4">
                        {imageUrl ? (
                          <div className="relative">
                            <img
                              src={imageUrl}
                              alt="Template"
                              className="w-32 h-32 object-cover rounded-lg border"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -top-2 -right-2 h-6 w-6"
                              onClick={() => setImageUrl(null)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30">
                            <ImageIcon className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 space-y-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={imageUploading}
                            className="hidden"
                            id="template-image-upload"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('template-image-upload')?.click()}
                            disabled={imageUploading}
                          >
                            {imageUploading ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            {imageUrl ? 'Change Photo' : 'Upload Photo'}
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            Add a photo to help identify this template
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Video URL */}
                    <div className="md:col-span-2">
                      <Label>Video Tutorial</Label>
                      <div className="mt-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              value={videoUrl || ''}
                              onChange={(e) => handleVideoUrlChange(e.target.value)}
                              placeholder="Paste YouTube URL..."
                              className="pl-10"
                            />
                          </div>
                          {videoUrl && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setVideoUrl(null)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {videoUrl && getYouTubeVideoId(videoUrl) && (
                          <div className="rounded-lg overflow-hidden border aspect-video">
                            <iframe
                              width="100%"
                              height="100%"
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(videoUrl)}`}
                              title="YouTube video"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Video className="h-3 w-3" />
                          Paste a YouTube link to embed a video tutorial for this recipe
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Ingredients */}
          <Collapsible
            open={sectionsOpen.ingredients}
            onOpenChange={(open) => setSectionsOpen((s) => ({ ...s, ingredients: open }))}
          >
            <div className="bg-card rounded-xl border">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
                <h3 className="font-semibold">
                  Ingredients ({(formData.ingredients || []).filter((i) => i.name).length})
                </h3>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 space-y-3">
                  {(formData.ingredients || []).map((ing, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-muted/50 rounded-lg p-3"
                    >
                      <Input
                        value={ing.name}
                        onChange={(e) => updateIngredient(index, { name: e.target.value })}
                        placeholder="Ingredient name"
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={ing.quantity}
                        onChange={(e) =>
                          updateIngredient(index, { quantity: parseFloat(e.target.value) || 0 })
                        }
                        className="w-20"
                      />
                      <Select
                        value={ing.unit}
                        onValueChange={(v) => updateIngredient(index, { unit: v })}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {unitOptions.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="relative w-24">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                          $
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={ing.cost_per_unit || ''}
                          onChange={(e) =>
                            updateIngredient(index, {
                              cost_per_unit: parseFloat(e.target.value) || undefined,
                            })
                          }
                          placeholder="0.00"
                          className="pl-5"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeIngredient(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  <Button variant="outline" onClick={addIngredient} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Ingredient
                  </Button>

                  {totalCost > 0 && (
                    <div className="pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
                      <span>
                        Total Cost: <strong className="text-foreground">${totalCost.toFixed(2)}</strong>
                      </span>
                      <span>
                        Cost per serving: <strong className="text-foreground">${costPerServing.toFixed(2)}</strong>
                      </span>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Instructions */}
          <Collapsible
            open={sectionsOpen.instructions}
            onOpenChange={(open) => setSectionsOpen((s) => ({ ...s, instructions: open }))}
          >
            <div className="bg-card rounded-xl border">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
                <h3 className="font-semibold">
                  Instructions ({(formData.instructions || []).filter((i) => i.description).length} steps)
                </h3>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 space-y-3">
                  {(formData.instructions || []).map((inst, index) => (
                    <div key={index} className="flex gap-3 bg-muted/50 rounded-lg p-3">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1 space-y-2">
                        <Textarea
                          value={inst.description}
                          onChange={(e) => updateInstruction(index, { description: e.target.value })}
                          rows={2}
                          placeholder="Describe this step..."
                        />
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Timer className="h-4 w-4 text-muted-foreground" />
                            <Input
                              type="number"
                              min="0"
                              value={inst.duration_minutes || ''}
                              onChange={(e) =>
                                updateInstruction(index, {
                                  duration_minutes: parseInt(e.target.value) || undefined,
                                })
                              }
                              placeholder="0"
                              className="w-16"
                            />
                            <span className="text-xs text-muted-foreground">min</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeInstruction(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button variant="outline" onClick={addInstruction} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Step
                  </Button>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Timing & Servings */}
          <Collapsible
            open={sectionsOpen.timing}
            onOpenChange={(open) => setSectionsOpen((s) => ({ ...s, timing: open }))}
          >
            <div className="bg-card rounded-xl border">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
                <h3 className="font-semibold">Timing & Servings</h3>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Prep Time (min)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.prep_time_minutes || ''}
                        onChange={(e) =>
                          updateField('prep_time_minutes', parseInt(e.target.value) || undefined)
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Cook Time (min)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.cook_time_minutes || ''}
                        onChange={(e) =>
                          updateField('cook_time_minutes', parseInt(e.target.value) || undefined)
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Servings</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.servings || ''}
                        onChange={(e) => updateField('servings', parseInt(e.target.value) || 1)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Difficulty</Label>
                      <Select
                        value={formData.difficulty_level || 'none'}
                        onValueChange={(v) => updateField('difficulty_level', v === 'none' ? undefined : v as any)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {difficultyOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Dietary Tags */}
          <Collapsible
            open={sectionsOpen.metadata}
            onOpenChange={(open) => setSectionsOpen((s) => ({ ...s, metadata: open }))}
          >
            <div className="bg-card rounded-xl border">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
                <h3 className="font-semibold">
                  Dietary Tags ({(formData.dietary_tags || []).length})
                </h3>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4">
                  <div className="flex flex-wrap gap-2">
                    {dietaryTagOptions.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => toggleDietaryTag(tag)}
                        className={cn(
                          'px-3 py-1.5 text-sm rounded-full border transition-colors',
                          (formData.dietary_tags || []).includes(tag)
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'border-border text-muted-foreground hover:bg-muted'
                        )}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>
      ) : (
        /* Preview Mode */
        <div className="bg-card rounded-xl border overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold">
                  {formData.name || 'Untitled Template'}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.category}
                  {formData.cuisine_type && ` • ${formData.cuisine_type}`}
                </p>
              </div>
              {formData.difficulty_level && (
                <span
                  className={cn(
                    'px-3 py-1 rounded-full text-sm font-medium',
                    formData.difficulty_level === 'easy' &&
                      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                    formData.difficulty_level === 'medium' &&
                      'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                    formData.difficulty_level === 'hard' &&
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  )}
                >
                  {formData.difficulty_level}
                </span>
              )}
            </div>

            {formData.description && (
              <p className="mt-4 text-muted-foreground">{formData.description}</p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              {totalTime > 0 && (
                <span className="flex items-center gap-1.5">
                  <Timer className="h-4 w-4" />
                  {totalTime} min total
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                {formData.servings} servings
              </span>
              {costPerServing > 0 && (
                <span className="flex items-center gap-1.5">
                  <DollarSign className="h-4 w-4" />
                  ${costPerServing.toFixed(2)}/serving
                </span>
              )}
            </div>

            {(formData.dietary_tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {(formData.dietary_tags || []).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 text-xs rounded-full bg-muted text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Scaling Control */}
          <div className="px-6 py-3 bg-muted/50 border-b flex items-center gap-4">
            <Scale className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium">Scale Recipe:</span>
            <div className="flex items-center gap-2">
              {[0.5, 1, 2, 3, 4].map((factor) => (
                <Button
                  key={factor}
                  variant={scaleFactor === factor ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setScaleFactor(factor)}
                >
                  {factor}x
                </Button>
              ))}
            </div>
            <span className="text-sm text-muted-foreground">
              ({Math.round((formData.servings || 4) * scaleFactor)} servings)
            </span>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Ingredients */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Ingredients</h3>
              <ul className="space-y-2">
                {scaledIngredients
                  .filter((ing) => ing.name)
                  .map((ing, index) => (
                    <li
                      key={index}
                      className={cn(
                        'flex items-start gap-3 text-muted-foreground',
                        ing.is_optional && 'opacity-70'
                      )}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span>
                        <strong className="text-foreground">
                          {ing.quantity % 1 === 0 ? ing.quantity : ing.quantity.toFixed(2)} {ing.unit}
                        </strong>{' '}
                        {ing.name}
                        {ing.is_optional && <span className="text-xs ml-1">(optional)</span>}
                      </span>
                    </li>
                  ))}
              </ul>

              {totalCost > 0 && (
                <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Total Cost (scaled):</span>
                    <strong className="text-foreground">${(totalCost * scaleFactor).toFixed(2)}</strong>
                  </div>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Instructions</h3>
              <ol className="space-y-4">
                {(formData.instructions || [])
                  .filter((inst) => inst.description)
                  .map((inst, index) => (
                    <li key={index} className="flex gap-4">
                      <div className="flex-shrink-0 h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-muted-foreground">{inst.description}</p>
                        {inst.duration_minutes && (
                          <p className="text-sm text-muted-foreground/70 mt-1 flex items-center gap-1">
                            <Timer className="h-3.5 w-3.5" />
                            {inst.duration_minutes} minutes
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

